import { Brain } from 'lucide-react';
import clsx from 'clsx';
import type { ThinkingGroup as ThinkingGroupType } from '../../types/chat';
import { ToolCallBlock } from './ToolCallBlock';

interface ThinkingGroupProps {
  group: ThinkingGroupType;
  isStreaming?: boolean;
}

export function ThinkingGroup({ group, isStreaming }: ThinkingGroupProps) {
  const hasTools = group.tools.length > 0;
  const hasThinking = group.thinking.trim().length > 0;

  // Don't render if no thinking text AND no tools
  if (!hasThinking && !hasTools) return null;

  return (
    <div className="mb-2">
      {/* Only show thinking section if there's thinking text */}
      {hasThinking && (
        <div
          className={clsx(
            'flex items-start gap-2 w-full px-3 py-2 rounded-lg',
            'bg-violet-50 text-violet-700 text-sm',
            'border border-violet-200'
          )}
        >
          <Brain className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
          <span className="flex-1 whitespace-pre-wrap">{group.thinking.trimStart()}</span>
          {isStreaming && (
            <span className="flex-shrink-0 self-end flex gap-0.5 ml-1">
              <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-pulse" />
              <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
            </span>
          )}
        </div>
      )}

      {/* Tools are always visible */}
      {hasTools && (
        <div className={clsx('space-y-2', hasThinking && 'mt-2 ml-4')}>
          {group.tools.map((tool, idx) => (
            <ToolCallBlock key={tool.id || idx} toolCall={tool} />
          ))}
        </div>
      )}
    </div>
  );
}
